/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { ThunkStatus } from '../../enums/ThunkStatus';
import { fetchDefaultVersion, fetchResource } from './configuration.thunk';
import { ConfigResourceProps } from '../../enums/ConfigResourceProps';
import { getFeesThunk } from './fees.thunk';

/* Initial state */
/**
 * Resources will have separate entries with their name as keys.
 * The procedure specific resources have entries for each procedure.
 *
 * version: {
 *   status: 'succeeded',
 *   value: 'develop'
 * },
 * resources: {
 *   "globalConfiguration": {
 *     value,
 *     status,
 *     error
 *   }
 *   .....
 *   "uiSchema": {
 *      "NATPAT": {
 *        value,
 *        status,
 *        error
 *      },
 *      "EPVL": {
 *        value,
 *        status,
 *        error
 *      },
 *      .....
 *   }
 * }
 */

const initialState = {
  version: {
    status: ThunkStatus.IDLE,
    value: undefined,
  },
  resources: {},
};

/* Matchers */

/**
 * support fetching of additional external configurations for pending
 * @param action
 * @returns {boolean}
 */
const fetchResourcePendingMatcher = action =>
  [fetchResource.pending.type, getFeesThunk.pending.type].includes(action.type);

/**
 * support fetching of additional external configurations for fulfilled
 * @param action
 * @returns {boolean}
 */
const fetchResourceFulfilledMatcher = action =>
  [fetchResource.fulfilled.type, getFeesThunk.fulfilled.type].includes(action.type);

/**
 * support fetching of additional external configurations for rejected
 * @param action
 * @returns {boolean}
 */
const fetchResourceRejectedMatcher = action =>
  [fetchResource.rejected.type, getFeesThunk.rejected.type].includes(action.type);

/* Slice */
export const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(fetchDefaultVersion.pending, (state) => {
        state.version.status = ThunkStatus.LOADING;
      })
      .addCase(fetchDefaultVersion.fulfilled, (state, action) => {
        state.version.status = ThunkStatus.SUCCEEDED;
        state.version.value = action.payload.defaultVersion.id;
        state.version.releaseVersion = action.payload.defaultVersion.releaseVersion;
      })
      .addCase(fetchDefaultVersion.rejected, (state) => {
        state.version.status = ThunkStatus.FAILED;
        state.version.value = undefined;
        state.version.releaseVersion = undefined;
      })
      .addMatcher(fetchResourcePendingMatcher, (state, action) => {
        const resourcePath = getResourcePath(getResourcePropsFromArg(action));
        _.set(state, `${resourcePath}.status`, ThunkStatus.LOADING);
      })
      .addMatcher(fetchResourceFulfilledMatcher, (state, action) => {
        const resourcePath = getResourcePath(getResourcePropsFromArg(action));
        _.set(state, `${resourcePath}.status`, ThunkStatus.SUCCEEDED);
        _.set(state, `${resourcePath}.value`, action.payload.value);
        _.set(state, `${resourcePath}.error`, undefined);
      })
      .addMatcher(fetchResourceRejectedMatcher, (state, action) => {
        const resourcePath = getResourcePath(getResourcePropsFromArg(action));
        _.set(state, `${resourcePath}.status`, ThunkStatus.FAILED);
        _.set(state, `${resourcePath}.value`, undefined);
        _.set(state, `${resourcePath}.error`, action.error.message);
      });
  },
});

const getResourcePropsFromArg = (action) => {
  const { procedure } = action.meta.arg;
  const { name } = action.meta.arg.configResource;
  return { resourceName: name, procedure };
};

const getResourcePath = ({ resourceName, procedure }) => {
  return `resources.${resourceName}${procedure ? `.${procedure}` : ''}`;
};

/* Selectors */
const selectConfiguration = state => state[configurationSlice.name];

export const selectVersion = createSelector(selectConfiguration, ({ version }) => version);

export const selectResource = createSelector(
  (state, resourceName, procedure) => {
    const pathParameters = { resourceName, procedure };
    return _.get(selectConfiguration(state), getResourcePath(pathParameters));
  },
  item => ({
    status: ThunkStatus.IDLE, // add a default IDLE status for non-existing item, overridden by deconstruction
    ...item,
  }),
);

export const selectGlobalConfiguration = createSelector(
  state => selectResource(state, ConfigResourceProps.GLOBAL_CONFIGURATION),
  state => state.value,
);

const selectProcedureResources = createSelector(
  selectGlobalConfiguration,
  (state, procedure) => procedure,
  (globalConfig, procedure) => globalConfig?.procedures[procedure]?.procedureResources,
);

export const selectEnabledProcedureIds = createSelector(selectGlobalConfiguration, globalConfig =>
  Object.values(globalConfig.procedures)
    .filter(({ enabled }) => enabled)
    .map(({ id }) => id),
);

export const selectProcedureResourceEnabled = createSelector(
  selectProcedureResources,
  (state, procedure, configName) => configName,
  (procedureResources, configName) => procedureResources?.includes(configName),
);

export const selectAutofillEnabled = createSelector(
  state => selectGlobalConfiguration(state),
  (state, procedure) => procedure,
  (globalConfig, procedure) => globalConfig?.procedures[procedure]?.autofill,
);

// TODO: This should be temporary, as we need to make all the forms configurable
// Once the EPCT creation form will be handled in the config files, then we can move the gdpr checbox there
export const selectGdprConfigRequired = createSelector(
  state => selectGlobalConfiguration(state),
  (state, procedure) => procedure,
  (globalConfig, procedure) => globalConfig?.procedures[procedure]?.gdprRequired,
);

export const selectFooterConfiguration = createSelector(
  selectGlobalConfiguration,
  globalConfig => globalConfig?.footerConfiguration,
);
